/* eslint-disable jsx-a11y/aria-role */
import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import {
  CarouselProvider, Slider, Slide, ButtonBack, ButtonNext,
} from 'pure-react-carousel';
import EventCard from '../EventCard';
import Icon from '../Icon';
import TranslationProvider, { getTranslation } from '../../providers/TranslationProvider';
import Chevron from '../../svg/icons/ic_chevron-left_20x20.svg';
import VenueShape from '../types/venue';
import appContextValues from '../../constants/appContext';
import UpcomingEventsSliderProgressRegion from './UpcomingEventsSliderProgressRegion';

import './UpcomingEvents.scss';

const { WIDGET, CONSUMER } = appContextValues;
const testId = 'upcoming-events-carousel';
const numberOfVisibleSliderInUpcomingSlider = 3;
const numberOfVisibleSliderInMobileUpcomingSlider = 2;

export default function UpcomingEvents({
  className,
  appContext,
  isMobile,
  // TODO: This previewMode prop does not seem to be putting anything
  // useful in the render, consider removing it.
  previewMode,
  upcomingEvents,
  venue,
}) {
  const noOfVisibleSlides = isMobile ? numberOfVisibleSliderInMobileUpcomingSlider : numberOfVisibleSliderInUpcomingSlider;

  if (upcomingEvents.length === 0) {
    return null;
  }

  const renderEvents = () => upcomingEvents.map(
    (eventData, index) => {
      const eventCard = (
        <EventCard
          className="UpcomingEvents__event-card"
          {...eventData}
          key={isMobile ? eventData.url_slug : null}
          appContext={appContext}
          isGda={eventData.is_global_dining_access}
          venue={{
            ...venue,
            locality: venue.location.city,
            neighborhood: venue.location.neighborhood,
          }}
        />
      );

      if (isMobile) {
        return eventCard;
      }
      return (
        <Slide
          aria-label={null}
          aria-selected={null}
          index={index}
          key={eventData.url_slug}
          tabIndex={-1}
          role={null}
        >
          {eventCard}
        </Slide>
      );
    }
  );

  const renderCarouselProviderButtons = () => {
    if (upcomingEvents.length < (isMobile ? 3 : 4)) {
      return null;
    }

    return (
      <div>
        <ButtonBack
          data-test-id={`${testId}--button--back`}
          aria-label={getTranslation({ translationKey: 'carousel_previous_label', fallback: 'Previous' })}
        >
          <div className="UpcomingEvents__carousel-button-container">
            <Icon
              component={Chevron}
              name="Chevron"
            />
          </div>
        </ButtonBack>
        <ButtonNext
          data-test-id={`${testId}--button--next`}
          aria-label={getTranslation({ translationKey: 'carousel_next_label', fallback: 'Next' })}
        >
          <div className="UpcomingEvents__carousel-button-container">
            <Icon
              component={Chevron}
              name="Chevron"
            />
          </div>
        </ButtonNext>
        <UpcomingEventsSliderProgressRegion upcomingEvents={upcomingEvents} visibleSlides={noOfVisibleSlides} />
      </div>
    );
  };

  const renderCarouselProvider = (renderedEvents) => (
    <CarouselProvider
      isIntrinsicHeight
      naturalSlideHeight={235}
      naturalSlideWidth={225}
      totalSlides={upcomingEvents.length}
      touchEnabled
      visibleSlides={noOfVisibleSlides}
    >
      <div className="UpcomingEvents__carousel-title">
        <TranslationProvider translationKey="upcoming_events">
          <h2 className="UpcomingEvents__title">
            Upcoming Events
          </h2>
        </TranslationProvider>

        { renderCarouselProviderButtons() }
      </div>

      <Slider
        classNameTray="UpcomingEvents__slider"
        aria-label={null}
        aria-live={null}
        role={null}
        tabIndex="-1"
      >
        {renderedEvents}
      </Slider>
    </CarouselProvider>

  );

  return (
    <div className={cx('UpcomingEvents', { [className]: className })}>
      {/* TODO: having this content in the render does not seem to be useful. */}
      {previewMode && <div className="UpcomingEvents--disabled" />}
      {renderCarouselProvider(
        renderEvents(upcomingEvents, isMobile)
      )}
    </div>
  );
}

UpcomingEvents.propTypes = {
  className: PropTypes.string,
  appContext: PropTypes.oneOf([WIDGET, CONSUMER]),
  isMobile: PropTypes.bool,
  // TODO: This previewMode prop does not seem to be putting anything
  // useful in the render, consider removing it.
  previewMode: PropTypes.bool,
  upcomingEvents: PropTypes.arrayOf(PropTypes.shape({
    date: PropTypes.string,
    end_time: PropTypes.string,
    general_sale_date: PropTypes.string,
    image_preview: PropTypes.string,
    is_global_dining_access: PropTypes.bool,
    name: PropTypes.string,
    responsive_images: PropTypes.shape({
      urls: PropTypes.shape(),
      file_names: PropTypes.arrayOf(PropTypes.string),
      aspect_ratios: PropTypes.shape(),
    }),
    time: PropTypes.string,
    url_slug: PropTypes.string,
  })).isRequired,
  venue: VenueShape.isRequired,
};

UpcomingEvents.defaultProps = {
  className: undefined,
  appContext: CONSUMER,
  isMobile: false,
  previewMode: false,
};
